import { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import "./App.css";
import CreateUserGroup from "./CreateUserGroup";
import AccessSearch from "./AccessSearch";
import AccessRequestHistory from "./AccessRequestHistory";
import EntitlementLookup from "./EntitlementLookup";
import ServiceNowActions from "./ServiceNowActions";
import SprintMetrics from "./SprintMetrics";
import ChangeLog from "./ChangeLog";
import HomePage from "./HomePage";
import CompareAccess from "./CompareAccess";
import DataAccessMappingPage from "./DataAccessMappingPage";
import PermissionComparator from "./PermissionComparator";
import ProvisioningErrors from "./ProvisioningErrors";
import SecurityAccessReport from "./SecurityAccessReport";
import AnalyticsDashboard from "./AnalyticsDashboard";
import AgentCatalog from "./AgentCatalog";
import ActorOrgUnitAssignment from "./ActorOrgUnitAssignment";
import InforUserPurge from "./InforUserPurge";
import TenantRefresh from "./TenantRefresh";
import { Archive } from "lucide-react";
import SecurityFieldTesting from "./SecurityFieldTesting";
import VisierSecurityTesting from "./VisierSecurityTesting";
import analytics from "./analytics";
import { oidcEnabled } from "./auth";

// ── CompareAccess constants ────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const FEEDBACK_CONTACT_EMAIL = (process.env.REACT_APP_CONTACT_EMAIL || "dhairya.shah@kochcc.com").trim();
const FEEDBACK_MAX_LENGTH = 1000;
const RELEASE_ANNOUNCEMENT_ID = "access-request-history-launch-2026-09";
const LOCAL_SIGNOUT_REDIRECT_URI = (
  process.env.REACT_APP_LOCAL_SIGNOUT_REDIRECT_URI || "https://www.kochinc.com/"
).trim();
const ENABLE_NAV_AUTHZ = process.env.REACT_APP_ENABLE_NAV_AUTHZ !== "false";
const ENABLE_DAS_MAPPING = process.env.REACT_APP_ENABLE_DAS_MAPPING === "true";
const ENABLE_TENANT_REFRESH = process.env.REACT_APP_ENABLE_TENANT_REFRESH === "true" && isDevHost();
const NAV_ACCESS_RULES_RAW = (process.env.REACT_APP_NAV_ACCESS_RULES_JSON || "").trim();
const NAV_AUTHZ_CLAIM_KEYS = (process.env.REACT_APP_NAV_AUTHZ_CLAIM_KEYS || "groups,roles,app_roles,permissions,governance_groups,kochid_entra_id_groups,kochid_groups")
  .split(",")
  .map(v => v.trim())
  .filter(Boolean);

const NAV_SUPERADMIN_EMAILS = (process.env.REACT_APP_NAV_SUPERADMIN_EMAILS || "")
  .split(",")
  .map(v => v.trim().toLowerCase())
  .filter(Boolean);

const NAV_SUPERADMIN_GROUP_SUBSTRINGS = (process.env.REACT_APP_NAV_SUPERADMIN_GROUP_SUBSTRINGS || "")
  .split(",")
  .map(v => v.trim().toLowerCase())
  .filter(Boolean);

// Superadmin = configured superadmin emails/groups, or the hrtsi_superadmin group the API authorizer checks.
function isSuperAdmin(profileClaims, userEmail) {
  const email = String(userEmail || "").trim().toLowerCase();
  if (email && NAV_SUPERADMIN_EMAILS.includes(email)) return true;
  const claims = profileClaims.map(v => v.toLowerCase());
  if (NAV_SUPERADMIN_GROUP_SUBSTRINGS.some(p => claims.some(c => c.includes(p)))) return true;
  return claims.some(c => c.trim() === "hrtsi_superadmin" || c.trim().endsWith(" - hrtsi_superadmin"));
}

const DEFAULT_NAV_ACCESS_RULES = {
  home: ["admin", "visitor"],
  "access-search": ["admin", "visitor"],
  "access-request-history": ["admin", "visitor"],
  "compare-access": ["admin", "visitor"],
  "security-access-report": ["admin", "visitor"],
  "security-field-testing": ["admin"],
  "visier-security-testing": ["admin"],
  comparator: ["admin"],
  "das-mapping": ["admin"],
  "create-user-group": ["admin"],
  "entitlement-lookup": ["admin"],
  "servicenow-actions": ["admin"],
  "sprint-metrics": ["admin"],
  "change-log": ["admin"],
  "provisioning-errors": ["admin"],
  analytics: ["admin"],
  "ai-agents": ["admin"],
  "actor-org-unit": ["admin"],
  "infor-user-purge": ["hrtsi_superadmin"],
  "tenant-refresh": ["hrtsi_superadmin"],
};

function isDevHost() {
  if (process.env.REACT_APP_SHOW_DEV_BANNER === "true") return true;

  const configuredHosts = (process.env.REACT_APP_DEV_HOSTS || "")
    .split(",")
    .map(h => h.trim().toLowerCase())
    .filter(Boolean);

  if (configuredHosts.length > 0) {
    const host = window.location.hostname.toLowerCase();
    if (configuredHosts.includes(host)) return true;
  }

  const host = window.location.hostname.toLowerCase();
  return host.includes("d2clpygy4t51sf.cloudfront.net") || host === "hrtsi-resource-center-dev.kochind.com";
}

function parseNavAccessRules() {
  if (!NAV_ACCESS_RULES_RAW) {
    return DEFAULT_NAV_ACCESS_RULES;
  }

  try {
    const parsed = JSON.parse(NAV_ACCESS_RULES_RAW);
    if (!parsed || typeof parsed !== "object") {
      return DEFAULT_NAV_ACCESS_RULES;
    }
    return { ...DEFAULT_NAV_ACCESS_RULES, ...parsed };
  } catch {
    return DEFAULT_NAV_ACCESS_RULES;
  }
}

function getProfileClaimValues(profile) {
  if (!profile) {
    return [];
  }

  const values = [];
  NAV_AUTHZ_CLAIM_KEYS.forEach((claimKey) => {
    const claimValue = profile[claimKey];
    if (Array.isArray(claimValue)) {
      claimValue.forEach((entry) => values.push(String(entry)));
    } else if (typeof claimValue === "string" && claimValue.trim()) {
      values.push(claimValue.trim());
    }
  });

  return [...new Set(values)];
}

function isNavItemAllowed(itemId, profileClaims, navAccessRules, userEmail) {
  if (!ENABLE_NAV_AUTHZ) {
    return true;
  }

  // Superadmin bypass - if user email is in superadmin list, grant all access
  if (userEmail && NAV_SUPERADMIN_EMAILS.length > 0) {
    const normalizedEmail = String(userEmail).trim().toLowerCase();
    if (NAV_SUPERADMIN_EMAILS.includes(normalizedEmail)) {
      return true;
    }
  }

  // Superadmin bypass - if user has superadmin group, grant all access
  if (NAV_SUPERADMIN_GROUP_SUBSTRINGS.length > 0) {
    const normalizedClaims = profileClaims.map(v => v.toLowerCase());
    const hasSuperadminGroup = NAV_SUPERADMIN_GROUP_SUBSTRINGS.some(pattern => 
      normalizedClaims.some(claim => claim.includes(pattern))
    );
    if (hasSuperadminGroup) {
      return true;
    }
  }

  const requiredPatterns = navAccessRules[itemId];
  if (!Array.isArray(requiredPatterns)) {
    return true; // Not configured, allow by default for backward compatibility
  }
  if (requiredPatterns.length === 0) {
    return false; // Empty array = restricted, deny access
  }

  const normalizedClaims = profileClaims.map(v => v.toLowerCase());
  return requiredPatterns.some((pattern) => {
    const normalizedPattern = String(pattern).trim().toLowerCase();
    if (!normalizedPattern) {
      return false;
    }
    return normalizedClaims.some((claim) => claim.includes(normalizedPattern));
  });
}

const API_TIMEOUT_MS = 45000;
const RATE_LIMIT_MAX_RETRIES = 5;
const RATE_LIMIT_BASE_DELAY_MS = 3000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseApiErrorText(text) {
  if (!text) return "Unknown API error";

  try {
    const parsed = JSON.parse(text);
    if (typeof parsed?.error === "string") {
      try {
        const nested = JSON.parse(parsed.error);
        return nested?.message || parsed.error;
      } catch {
        return parsed.error;
      }
    }
    return parsed?.message || parsed?.error || text;
  } catch {
    return text;
  }
}

function computeRetryDelayMs(retriesLeft, retryAfterHeader) {
  const retryAfterSeconds = Number(retryAfterHeader);
  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return retryAfterSeconds * 1000;
  }

  const attempt = RATE_LIMIT_MAX_RETRIES - retriesLeft;
  const backoff = RATE_LIMIT_BASE_DELAY_MS * (2 ** Math.max(0, attempt - 1));
  const jitter = Math.floor(Math.random() * 500);
  return backoff + jitter;
}

async function apiFetch(path, retries = RATE_LIMIT_MAX_RETRIES) {
  const sep = path.includes("?") ? "&" : "?";
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  try {
    const res = await fetch(`${API_BASE}${path}${sep}_=${Date.now()}`, {
      signal: controller.signal,
    });

    if (!res.ok) {
      const text = await res.text();

      if (res.status === 429 && retries > 0) {
        const waitMs = computeRetryDelayMs(retries, res.headers.get("Retry-After"));
        await sleep(waitMs);
        return apiFetch(path, retries - 1);
      }

      const parsedMessage = parseApiErrorText(text);
      throw new Error(`API error ${res.status}: ${parsedMessage}`);
    }

    return res.json();
  } catch (err) {
    if (err?.name === "AbortError") {
      if (retries > 0) {
        await sleep(1200);
        return apiFetch(path, retries - 1);
      }
      throw new Error(`API request timed out for ${path}`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
// ── Navigation config ───────────────────────────────────────────────────────
const NAV = [
  {
    id: "home",
    label: "Home",
    sub: "Resource Center",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    id: "comparator",
    label: "Permission Comparator",
    sub: "Production vs Draft",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    id: "das-mapping",
    label: "Data Access Mapping",
    sub: "Single project view",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18"/><path d="M3 12h18"/><path d="M3 18h18"/>
        <circle cx="8" cy="6" r="1.5"/><circle cx="16" cy="12" r="1.5"/><circle cx="10" cy="18" r="1.5"/>
      </svg>
    ),
  },
  {
    id: "create-user-group",
    label: "Visier Security Manager",
    sub: "Groups · Permissions · Runbook",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <line x1="19" y1="8" x2="19" y2="14"/>
        <line x1="22" y1="11" x2="16" y2="11"/>
      </svg>
    ),
  },
  {
    id: "compare-access",
    label: "Compare Access",
    sub: "Up to 5 employees side-by-side",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    id: "entitlement-lookup",
    label: "Entitlement Lookup",
    sub: "Profile → Assigned Identities",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    id: "access-search",
    label: "Access Search",
    sub: "Employee Access Profiles",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
  },
  {
    id: "access-request-history",
    label: "Access Request History",
    sub: "Grant & Revoke History",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
    ),
  },
  {
    id: "security-access-report",
    label: "Security Access Report",
    sub: "myHR Security Access",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18"/>
        <rect x="7" y="10" width="3" height="8"/>
        <rect x="14" y="5" width="3" height="13"/>
      </svg>
    ),
  },
  {
    id: "security-field-testing",
    label: "Security Field Testing",
    sub: "myHR Field Access Validation",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        <path d="M12 16.5v1"/>
      </svg>
    ),
  },
  {
    id: "visier-security-testing",
    label: "Visier Security Testing",
    sub: "Permission Profile Validation",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <path d="M9 12l2 2 4-4"/>
      </svg>
    ),
  },
  {
    id: "servicenow-actions",
    label: "ServiceNow Actions",
    sub: "Sprints & Story Management",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/>
        <rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/>
        <rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    id: "sprint-metrics",
    label: "Sprint Metrics",
    sub: "Snapshots & unplanned work",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18"/>
        <path d="M7 15l4-4 3 3 5-7"/>
      </svg>
    ),
  },
  {
    id: "change-log",
    label: "Change Log",
    sub: "Production promotion checklist",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4"/>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
  },
  {
    id: "provisioning-errors",
    label: "Provisioning Errors",
    sub: "Failed provisioning events",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
        <path d="M12 8v4"/>
        <path d="M12 16h.01"/>
      </svg>
    ),
  },
  {
    id: "analytics",
    label: "Usage & Analytics",
    sub: "User activity & statistics",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18"/>
        <path d="M18 17V9"/>
        <path d="M13 17V5"/>
        <path d="M8 17v-3"/>
      </svg>
    ),
  },
  {
    id: "ai-agents",
    label: "AI Agents",
    sub: "Copilot Studio agent catalog",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h-2a5 5 0 0 0-5-5h-1v1.27c.6.34 1 .99 1 1.73 0 1.1-.9 2-2 2s-2-.9-2-2c0-.74.4-1.39 1-1.73V9H9a5 5 0 0 0-5 5H2a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 15v7"/>
        <circle cx="12" cy="12" r="1"/>
      </svg>
    ),
  },
  {
    id: "actor-org-unit",
    label: "Assign Org Units",
    sub: "Assign org units to actors",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="8.5" cy="7" r="4"/>
        <polyline points="17 11 19 13 23 9"/>
      </svg>
    ),
  },
  {
    id: "tenant-refresh",
    label: "Tenant Refresh",
    sub: "AX5 Pre-refresh Backup",
    icon: <Archive size={17} />,
  },
  {
    id: "infor-user-purge",
    label: "Infor User Purge",
    sub: "Remove purged employees from IFS",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6"/><path d="M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
      </svg>
    ),
  },
];

export default function App() {
  if (oidcEnabled) {
    return <OidcApp />;
  }

  return <AppShell />;
}

function OidcApp() {
  const auth = useAuth();

  useEffect(() => {
    window.__VP_AUTH_TOKEN = auth.user?.id_token || "";
    return () => {
      window.__VP_AUTH_TOKEN = "";
    };
  }, [auth.user?.id_token]);

  if (auth.isLoading) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-brand">H</div>
          <h1 className="auth-title">HRT Security & Infrastructure Resource Center</h1>
          <p className="auth-copy">Loading PingOne sign-in...</p>
        </div>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-brand">V</div>
          <h1 className="auth-title">Sign-in error</h1>
          <p className="auth-copy">{auth.error.message}</p>
          <button className="auth-button" onClick={() => auth.signinRedirect()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-brand">H</div>
          <h1 className="auth-title">HRT Security & Infrastructure Resource Center</h1>
          <p className="auth-copy">Sign in with PingOne to access the dev application.</p>
          <button className="auth-button" onClick={() => auth.signinRedirect()}>
            Sign in with PingOne
          </button>
        </div>
      </div>
    );
  }

  const profileName = auth.user?.profile?.name || auth.user?.profile?.preferred_username || auth.user?.profile?.email;
  const handleLocalSignOut = () => {
    const fallbackUrl = `${window.location.origin}/`;
    const targetUrl = LOCAL_SIGNOUT_REDIRECT_URI || fallbackUrl;
    window.__VP_AUTH_TOKEN = "";

    Promise.resolve(auth.removeUser()).finally(() => {
      window.location.assign(targetUrl);
    });
  };

  return <AppShell profileName={profileName} profile={auth.user?.profile} onSignOut={handleLocalSignOut} />;
}

function AppShell({ profileName, profile, onSignOut }) {
  const [active, setActive] = useState("home");
  const [collapsed, setCollapsed] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const showDevPreview = isDevHost();
  const navAccessRules = parseNavAccessRules();
  const profileClaims = getProfileClaimValues(profile);
  const userEmail = profile?.email || profile?.preferred_username || "";
  const announcementUserId = profile?.sub || userEmail;
  const announcementStorageKey = `hrtsi-announcement:${RELEASE_ANNOUNCEMENT_ID}:${announcementUserId}`;
  const canViewAnnouncement = ENABLE_NAV_AUTHZ && ["analytics", "access-request-history"].some(
    (pageId) => isNavItemAllowed(pageId, profileClaims, navAccessRules, userEmail)
  );
  const [showReleaseAnnouncement, setShowReleaseAnnouncement] = useState(false);

  useEffect(() => {
    if (!announcementUserId || !canViewAnnouncement) {
      setShowReleaseAnnouncement(false);
      return;
    }

    try {
      setShowReleaseAnnouncement(localStorage.getItem(announcementStorageKey) !== "dismissed");
    } catch {
      setShowReleaseAnnouncement(true);
    }
  }, [announcementStorageKey, announcementUserId, canViewAnnouncement]);

  // Initialize analytics with user profile
  useEffect(() => {
    if (profile) {
      analytics.setUser(profile);
    }
  }, [profile]);

  // Track page views when active page changes (only if user is authenticated)
  useEffect(() => {
    if (active && profile) {
      analytics.trackPageView(active);
    }
  }, [active, profile]);
  const isTenantRefreshVisitor = profileClaims.some(claim => claim.toLowerCase().includes("hrtsi_visitor"));
  const canNavigate = (itemId) => {
    if (itemId === "tenant-refresh" && isTenantRefreshVisitor) return false;
    return isNavItemAllowed(itemId, profileClaims, navAccessRules, userEmail);
  };
  const visibleNavItems = NAV.filter((item) => {
    if (item.id === "tenant-refresh" && !ENABLE_TENANT_REFRESH) {
      return false;
    }
    if (item.id === "das-mapping" && !ENABLE_DAS_MAPPING) {
      return false;
    }
    return canNavigate(item.id);
  });
  const sessionActions = profileName ? (
    <div className="home-session-actions app-session-actions">
      <span className="home-session-label">Signed in as</span>
      <span className="home-session-value">{profileName}</span>
      {onSignOut && (
        <button className="home-session-signout" onClick={onSignOut}>Sign out</button>
      )}
    </div>
  ) : null;

  useEffect(() => {
    if (!visibleNavItems.some(item => item.id === active)) {
      setActive(visibleNavItems[0]?.id || "home");
    }
  }, [active, visibleNavItems]);

  const openFeedbackDialog = () => {
    setFeedbackError("");
    setFeedbackStatus("");
    setIsFeedbackOpen(true);
  };

  const closeFeedbackDialog = () => {
    if (feedbackSubmitting) return;
    setIsFeedbackOpen(false);
  };

  const dismissReleaseAnnouncement = () => {
    try {
      localStorage.setItem(announcementStorageKey, "dismissed");
    } catch {
      // Keep the dismissal for this app session if browser storage is unavailable.
    }
    setShowReleaseAnnouncement(false);
  };

  const openHistoryFromAnnouncement = () => {
    dismissReleaseAnnouncement();
    if (isNavItemAllowed("access-request-history", profileClaims, navAccessRules, userEmail)) {
      setActive("access-request-history");
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    const cleanedMessage = feedbackMessage.trim();

    if (!cleanedMessage) {
      setFeedbackError("Please enter a feedback message before submitting.");
      setFeedbackStatus("");
      return;
    }

    setFeedbackSubmitting(true);
    setFeedbackError("");
    setFeedbackStatus("");

    try {
      const response = await fetch(`${API_BASE}/feedback`, {
        method: "POST",
        body: JSON.stringify({
          message: cleanedMessage,
          pageUrl: window.location.href,
          contactEmail: FEEDBACK_CONTACT_EMAIL,
          submittedAt: new Date().toISOString(),
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const errorMessage = payload?.error || payload?.message || `Feedback submission failed (${response.status}).`;
        throw new Error(errorMessage);
      }

      setFeedbackMessage("");
      setFeedbackStatus("Feedback sent successfully.");
    } catch (err) {
      setFeedbackError(err?.message || "Unable to submit feedback right now. Please try again.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="root-shell">
      {/* ── Sidebar ── */}
      <aside className={`sidebar ${collapsed ? "sidebar-collapsed" : ""}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-stack">
            {!collapsed && (
              <div className="sidebar-brand-logo-wrap">
                <img
                  src={`${process.env.PUBLIC_URL || ""}/Logos/Koch-Technology-Lockup-horz-digital-black.ai.png`}
                  alt="Koch Technology"
                  className="sidebar-brand-logo"
                />
              </div>
            )}
            {collapsed && (
              <div className="logo-mark sidebar-brand-mark">K</div>
            )}
          </div>
          <button className="sidebar-collapse-btn" onClick={() => setCollapsed(c => !c)} title={collapsed ? "Expand" : "Collapse"}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {collapsed
                ? <><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></>
                : <><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></>}
            </svg>
          </button>
        </div>
        {!collapsed && showDevPreview && (
          <div className="sidebar-dev-badge-bar">
            <span className="sidebar-dev-badge">DEV PREVIEW</span>
          </div>
        )}

        <nav className="sidebar-nav">
          {visibleNavItems.map(item => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${active === item.id ? "sidebar-nav-item-active" : ""}`}
              onClick={() => setActive(item.id)}
              title={collapsed ? item.label : ""}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              {!collapsed && (
                <span className="sidebar-nav-text">
                  <span className="sidebar-nav-label">{item.label}</span>
                  <span className="sidebar-nav-sub">{item.sub}</span>
                </span>
              )}
              {active === item.id && <span className="sidebar-active-bar" />}
            </button>
          ))}
        </nav>

        <div className="sidebar-feedback-wrap">
          <button
            className="sidebar-feedback-link"
            onClick={openFeedbackDialog}
            title="Provide feedback"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {!collapsed && <span>Provide feedback</span>}
          </button>
        </div>
      </aside>

      {/* ── Main panel ── */}
      <div className="root-main">
        <div className="root-page">
          {sessionActions && (
            <div className="app-session-floating">
              {sessionActions}
            </div>
          )}
          {active === "home"                && <HomePage onNavigate={setActive} enableDasMapping={ENABLE_DAS_MAPPING} enableTenantRefresh={ENABLE_TENANT_REFRESH && !isTenantRefreshVisitor} isNavItemAllowed={canNavigate} />}
          {active === "comparator"          && <PermissionComparator apiFetch={apiFetch} />}
          {ENABLE_DAS_MAPPING && active === "das-mapping" && <DataAccessMappingPage />}
          {active === "compare-access"      && <CompareAccess />}
          {active === "create-user-group"   && <CreateUserGroup isSuperAdmin={isSuperAdmin(profileClaims, userEmail)} />}
          {active === "entitlement-lookup"  && <EntitlementLookup />}
          {active === "access-search"       && <AccessSearch />}
          {active === "access-request-history" && <AccessRequestHistory />}
          {active === "security-access-report" && <SecurityAccessReport />}
          {active === "security-field-testing" && <SecurityFieldTesting />}
          {active === "visier-security-testing" && <VisierSecurityTesting />}
          {active === "servicenow-actions"  && <ServiceNowActions />}
          {active === "sprint-metrics"      && <SprintMetrics />}
          {active === "change-log"          && <ChangeLog />}
          {active === "provisioning-errors" && <ProvisioningErrors />}
          {active === "analytics"           && <AnalyticsDashboard />}
          {active === "ai-agents"           && <AgentCatalog />}
          {active === "actor-org-unit"      && <ActorOrgUnitAssignment />}
          {active === "infor-user-purge"    && <InforUserPurge />}
          {ENABLE_TENANT_REFRESH && !isTenantRefreshVisitor && active === "tenant-refresh" && <TenantRefresh />}
        </div>
      </div>

      {isFeedbackOpen && (
        <div className="modal-overlay" onClick={closeFeedbackDialog}>
          <div className="modal-card feedback-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Provide Feedback</h3>
              <button className="modal-close-btn" onClick={closeFeedbackDialog} disabled={feedbackSubmitting}>×</button>
            </div>

            <form onSubmit={handleFeedbackSubmit}>
              <div className="modal-body">
                <p className="feedback-modal-copy">
                  Share suggestions, bugs, or improvements. Submitted feedback is emailed to
                  {" "}<a href={`mailto:${FEEDBACK_CONTACT_EMAIL}`} className="feedback-modal-link">{FEEDBACK_CONTACT_EMAIL}</a>.
                </p>
                <textarea
                  className="cug-input cug-textarea feedback-modal-textarea"
                  placeholder="Provide feedback"
                  value={feedbackMessage}
                  onChange={(e) => {
                    setFeedbackMessage(e.target.value);
                    if (feedbackError) setFeedbackError("");
                    if (feedbackStatus) setFeedbackStatus("");
                  }}
                  maxLength={FEEDBACK_MAX_LENGTH}
                  disabled={feedbackSubmitting}
                />
                {feedbackError && <div className="app-footer-error feedback-modal-msg">{feedbackError}</div>}
                {feedbackStatus && <div className="app-footer-status feedback-modal-msg">{feedbackStatus}</div>}
              </div>

              <div className="modal-footer feedback-modal-footer">
                <button
                  type="button"
                  className="feedback-modal-cancel"
                  onClick={closeFeedbackDialog}
                  disabled={feedbackSubmitting}
                >
                  Close
                </button>
                <button type="submit" className="compare-btn" disabled={feedbackSubmitting}>
                  {feedbackSubmitting ? "Sending..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReleaseAnnouncement && (
        <div className="modal-overlay release-announcement-overlay" onClick={dismissReleaseAnnouncement}>
          <section
            className="modal-card release-announcement-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="release-announcement-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="release-announcement-title">What’s New</h3>
              <button
                className="modal-close-btn"
                onClick={dismissReleaseAnnouncement}
                aria-label="Dismiss announcement"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <h4 className="release-announcement-heading">Access Request History</h4>
              <p className="release-announcement-copy">
                Review an employee’s GRANT and REVOKE history, including request dates, approvals, and status. Search by name, then select the correct employee.
              </p>
            </div>
            <div className="modal-footer release-announcement-footer">
              <button className="feedback-modal-cancel" onClick={dismissReleaseAnnouncement}>
                Dismiss
              </button>
              <button className="compare-btn" onClick={openHistoryFromAnnouncement}>
                Open Access Request History
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}








